import dayjs, { Dayjs } from "dayjs";
import {
	ApiResponse,
	EventDetailFormData,
	EventFormData,
	EventGetApiResponse,
	EventListApiResponse,
	VolunteersEventType,
	UpdateEventRequest,
	UpdateEventPreview,
	EventPreviewFormData,
	UpdateEventPreviewLocalizable,
	EventPreviewLocalizableFormData,
	UpdateEventDetail,
	UpdateEventDetailLocalizable,
	EventDetailLocalizableFormData
} from "../context/EventContext/constants";
import { axiosInstance } from "../utils/axiosInstance";

const ENDPOINTS = {
	EVENT: "/cms/event",
};

const appendIfValue = (formData: FormData, key: string, value: string | number | boolean | Date | Dayjs | null | undefined) => {
	if (value === undefined || value === null) {
		return;
	}
	if (typeof value === "string" && !value.trim()) {
		return;
	}
	if (dayjs.isDayjs(value)) {
		formData.append(key, value.toISOString());
		return;
	}
	formData.append(key, value instanceof Date ? value.toISOString() : value.toString());
};

const appendPreview = (formData: FormData, preview: UpdateEventPreview | EventPreviewFormData, isUpdate: boolean) => {
	const prefix = "eventPreview";

	if (isUpdate) {
		appendIfValue(formData, `${prefix}.eventPreviewId`, (preview as UpdateEventPreview).eventPreviewId);
	}
	if (preview.displayOrder !== undefined) {
		appendIfValue(formData, `${prefix}.displayOrder`, preview.displayOrder);
	}
	if (preview.isFeatured !== undefined) {
		formData.append(`${prefix}.isFeatured`, preview.isFeatured ? "true" : "false");
	}

	if (preview.eventPreviewLocalizable && preview.eventPreviewLocalizable.length > 0) {
		preview.eventPreviewLocalizable.forEach((item: UpdateEventPreviewLocalizable | EventPreviewLocalizableFormData, index: number) => {
			const itemPrefix = `${prefix}.eventPreviewLocalizable[${index}]`;
			if (isUpdate) {
				const updatePreviewLocalizableRequest = item as UpdateEventPreviewLocalizable;
				appendIfValue(formData, `${itemPrefix}.eventPreviewLocalizableId`, updatePreviewLocalizableRequest.eventPreviewLocalizableId);
			}
			appendIfValue(formData, `${itemPrefix}.culture`, item.culture);
			appendIfValue(formData, `${itemPrefix}.header`, item.header);
			appendIfValue(formData, `${itemPrefix}.previewText`, item.previewText);
			appendIfValue(formData, `${itemPrefix}.categoryTag`, item.categoryTag);
			if (item.image) {
				formData.append(`${itemPrefix}.image`, item.image);
			}
		});
	}
};

const appendDetail = (formData: FormData, detail: UpdateEventDetail | EventDetailFormData, isUpdate: boolean) => {
	const prefix = "eventDetail";

	if (isUpdate) {
		appendIfValue(formData, `${prefix}.eventDetailId`, (detail as UpdateEventDetail).eventDetailId);
	}
	if (detail.hasCustomButton !== undefined) {
		formData.append(`${prefix}.hasCustomButton`, detail.hasCustomButton ? "true" : "false");
	}

	if (detail.eventDetailLocalizable && detail.eventDetailLocalizable.length > 0) {
		detail.eventDetailLocalizable.forEach((item: EventDetailLocalizableFormData | UpdateEventDetailLocalizable, index: number) => {
			const itemPrefix = `${prefix}.eventDetailLocalizable[${index}]`;
			if (isUpdate) {
				appendIfValue(formData, `${itemPrefix}.eventDetailLocalizableId`, (item as UpdateEventDetailLocalizable).eventDetailLocalizableId);
			}
			appendIfValue(formData, `${itemPrefix}.culture`, item.culture);
			appendIfValue(formData, `${itemPrefix}.header`, item.header);
			appendIfValue(formData, `${itemPrefix}.detailText`, item.detailText);
			appendIfValue(formData, `${itemPrefix}.applicantText`, item.applicantText);
			appendIfValue(formData, `${itemPrefix}.processText`, item.processText);
			appendIfValue(formData, `${itemPrefix}.durationText`, item.durationText);
			appendIfValue(formData, `${itemPrefix}.customButtonText`, item.customButtonText);
			appendIfValue(formData, `${itemPrefix}.customButtonUrl`, item.customButtonUrl);
			if (item.image) {
				formData.append(`${itemPrefix}.image`, item.image);
			}
		});
	}
};

const buildEventFormData = (eventData: EventFormData | UpdateEventRequest, isUpdate: boolean): FormData => {
	const formData = new FormData();

	if (isUpdate) {
		const updateRequest = eventData as UpdateEventRequest;
		formData.append("eventId", updateRequest.eventId ?? "");
	}

	appendIfValue(formData, "eventType", eventData.eventType);
	appendIfValue(formData, "projectDateText", eventData.projectDateText);
	appendIfValue(formData, "locationText", eventData.locationText);

	const isApplicationRequired = eventData.eventType === VolunteersEventType.ApplicationRequired;

	if (!isUpdate) {
		const addEventData = eventData as EventFormData;

		if (isApplicationRequired) {
			appendIfValue(formData, "projectDate", addEventData.projectDate);
			if (addEventData.isAppliable !== undefined) {
				formData.append("isAppliable", addEventData.isAppliable ? "true" : "false");
			}
		} else {
			appendIfValue(formData, "categoryTag", addEventData.categoryTag);
		}

		const detail: EventDetailFormData = {
			...addEventData.eventDetail,
			hasCustomButton: isApplicationRequired ? false : addEventData.eventDetail.hasCustomButton,
			eventDetailLocalizable: addEventData.eventDetail.eventDetailLocalizable.map(item => ({
				...item,
				durationText: isApplicationRequired ? item.durationText : "",
				customButtonText: !isApplicationRequired && addEventData.eventDetail.hasCustomButton ? item.customButtonText : "",
				customButtonUrl: !isApplicationRequired && addEventData.eventDetail.hasCustomButton ? item.customButtonUrl : "",
			})),
		};

		if (eventData.eventTagIds) {
			eventData.eventTagIds.forEach((tagId: string, index: number) => {
				formData.append(`eventTagIds[${index}]`, tagId);
			});
		}

		appendPreview(formData, addEventData.eventPreview, false);
		appendDetail(formData, detail, false);
	} else {
		const updateRequest = eventData as UpdateEventRequest;

		if (updateRequest.projectDate !== undefined) {
			appendIfValue(formData, "projectDate", updateRequest.projectDate);
		}
		if (updateRequest.isAppliable !== undefined) {
			formData.append("isAppliable", updateRequest.isAppliable ? "true" : "false");
		}
		if (updateRequest.isActive !== undefined) {
			formData.append("isActive", updateRequest.isActive ? "true" : "false");
		}
		if (updateRequest.categoryTag !== undefined) {
			appendIfValue(formData, "categoryTag", updateRequest.categoryTag);
		}

		if (updateRequest.eventPreview) {
			appendPreview(formData, updateRequest.eventPreview, true);
		}
		if (updateRequest.eventDetail) {
			appendDetail(formData, updateRequest.eventDetail, true);
		}

		if (updateRequest.eventTagIds) {
			if (updateRequest.eventTagIds.length === 0) {
				formData.append("clearEventTags", "true");
			} else {
				updateRequest.eventTagIds.forEach((tagId: string, index: number) => {
					formData.append(`eventTagIds[${index}]`, tagId);
				});
			}
		}
	}

	return formData;
};

export class EventService {
	static async getEventList(): Promise<EventListApiResponse> {
		const response = await axiosInstance.get(ENDPOINTS.EVENT);
		return response.data;
	}

	static async getEventDetails(eventId: string): Promise<EventGetApiResponse> {
		const response = await axiosInstance.get(`${ENDPOINTS.EVENT}/${eventId}`);
		return response.data;
	}

	static async addEvent(eventData: EventFormData): Promise<ApiResponse<unknown>> {
		const response = await axiosInstance.post(ENDPOINTS.EVENT, buildEventFormData(eventData, false));
		return response.data;
	}

	static async updateEvent(eventData: UpdateEventRequest): Promise<ApiResponse<unknown>> {
		const response = await axiosInstance.patch(ENDPOINTS.EVENT, buildEventFormData(eventData, true));
		return response.data;
	}

	static async removeEvent(eventId: string): Promise<ApiResponse<unknown>> {
		const response = await axiosInstance.delete(`${ENDPOINTS.EVENT}/${eventId}`);

		if (response.status === 204) {
			return { result: null, error: null, isSuccessful: true };
		}

		return response.data;
	}
}
